import type { Booking, BookingStatus } from '@/lib/types';
import { generateNumericId } from '@/lib/utils';
import { format, isAfter, isBefore, parseISO } from 'date-fns';
import { isSampleDataMode } from '@/lib/config/env';
import { FilterBuilder } from '@/lib/utils/apiHelpers';
import { sampleBookingsData } from '@/lib/sample-data/bookings';

interface CreateBookingData extends Omit<Booking, 'id' | 'dateCreated' | 'dateUpdated'> {
  // Additional booking creation fields can be added here
  priority?: 'normal' | 'high';
}

interface BookingFilters {
  spaceId?: string;
  userUid?: string;
  status?: BookingStatus;
  startDate?: string;
  endDate?: string;
  search?: string;
}

/**
 * Booking manager that unifies booking operations
 */
export class BookingManager {
  private demoBookings: Booking[] = [];
  private readonly DELAY_MS = 100;

  constructor() {
    this.initializeSampleData();
  }

  /**
   * Get all bookings with unified logic
   */
  async getAllBookings(): Promise<Booking[]> {
    return this.withDelay(async () => {
      if (isSampleDataMode()) {
        return [...this.demoBookings];
      }

      // In production, would integrate with actual booking service
      console.log('BookingManager: Production mode - would fetch from Firebase');
      return [];
    });
  }

  /**
   * Get bookings with flexible filtering
   */
  async getBookings(filters: BookingFilters = {}): Promise<Booking[]> {
    const allBookings = await this.getAllBookings();
    
    return new FilterBuilder(allBookings)
      .where('spaceId', filters.spaceId)
      .where('userUid', filters.userUid)
      .where('status', filters.status)
      .custom(booking => this.isInDateRange(booking, filters.startDate, filters.endDate))
      .search(filters.search, ['title', 'notes'])
      .build();
  }

  /**
   * Get booking by ID
   */
  async getBookingById(id: string): Promise<Booking | undefined> {
    return this.withDelay(async () => {
      const allBookings = await this.getAllBookings();
      return allBookings.find(booking => booking.id === id);
    });
  }

  /**
   * Create new booking
   */
  async createBooking(bookingData: CreateBookingData): Promise<Booking> {
    return this.withDelay(async () => {
      // Validate booking conflicts
      const conflicts = await this.checkBookingConflicts(bookingData);
      if (conflicts.length > 0) {
        throw new Error(`Booking conflicts with existing reservations: ${conflicts.map(c => c.id).join(', ')}`);
      }

      const now = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
      const newBooking: Booking = {
        id: generateNumericId(10),
        ...bookingData,
        dateCreated: now,
        dateUpdated: now,
      };

      if (isSampleDataMode()) {
        this.demoBookings.push(newBooking);
        return JSON.parse(JSON.stringify(newBooking));
      }

      // In production, would create through Firebase
      console.log('BookingManager: Production mode - would create in Firebase');
      return newBooking;
    });
  }

  /**
   * Update existing booking
   */
  async updateBooking(id: string, updates: Partial<Booking>): Promise<Booking | null> {
    return this.withDelay(async () => {
      const existingBooking = await this.getBookingById(id);
      if (!existingBooking) return null;

      // Validate conflicts if time/space changed
      if (updates.spaceId || updates.date || updates.startTime || updates.endTime) {
        const testBooking = { ...existingBooking, ...updates };
        const conflicts = await this.checkBookingConflicts(testBooking, id);
        if (conflicts.length > 0) {
          throw new Error(`Update would create conflicts: ${conflicts.map(c => c.id).join(', ')}`);
        }
      }

      const updatedBooking = {
        ...existingBooking,
        ...updates,
        dateUpdated: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
      };

      if (isSampleDataMode()) {
        const index = this.demoBookings.findIndex(b => b.id === id);
        if (index !== -1) {
          this.demoBookings[index] = updatedBooking;
        }
        return JSON.parse(JSON.stringify(updatedBooking));
      }

      // In production, would update through Firebase
      console.log('BookingManager: Production mode - would update in Firebase');
      return updatedBooking;
    });
  }

  /**
   * Delete booking
   */
  async deleteBooking(id: string): Promise<boolean> {
    return this.withDelay(async () => {
      if (isSampleDataMode()) {
        const index = this.demoBookings.findIndex(b => b.id === id);
        if (index !== -1) {
          this.demoBookings.splice(index, 1);
          return true;
        }
        return false;
      }

      // In production, would delete through Firebase
      console.log('BookingManager: Production mode - would delete from Firebase');
      return true;
    });
  }

  /**
   * Get bookings for a specific space and date range
   */
  async getBookingsForSpace(spaceId: string, startDate: string, endDate: string): Promise<Booking[]> {
    return this.getBookings({
      spaceId,
      startDate,
      endDate,
    });
  }

  /**
   * Get bookings for a specific user
   */
  async getUserBookings(userUid: string): Promise<Booking[]> {
    return this.getBookings({ userUid });
  }

  /**
   * Check for booking conflicts
   */
  async checkBookingConflicts(booking: CreateBookingData | Booking, excludeId?: string): Promise<Booking[]> {
    const existingBookings = await this.getBookingsForSpace(
      booking.spaceId,
      booking.date,
      booking.date
    );

    return existingBookings.filter(existing => {
      // Skip the booking being updated
      if (excludeId && existing.id === excludeId) return false;
      
      // Skip cancelled bookings
      if (existing.status === 'Cancelled') return false;

      // Check time overlap
      return this.hasTimeOverlap(booking, existing);
    });
  }

  /**
   * Get available time slots for a space on a date
   */
  async getAvailableSlots(spaceId: string, date: string): Promise<Array<{ startTime: string; endTime: string; }>> {
    const bookings = await this.getBookingsForSpace(spaceId, date, date);
    const activeBookings = bookings.filter(b => b.status !== 'Cancelled');

    // Generate all possible 1-hour slots from 6 AM to 11 PM
    const allSlots: Array<{ startTime: string; endTime: string; }> = [];
    for (let hour = 6; hour < 23; hour++) {
      const startTime = `${hour.toString().padStart(2, '0')}:00`;
      const endTime = `${(hour + 1).toString().padStart(2, '0')}:00`;
      allSlots.push({ startTime, endTime });
    }

    // Filter out booked slots
    return allSlots.filter(slot => {
      return !activeBookings.some(booking => this.hasTimeOverlap(slot, booking));
    });
  }

  /**
   * Get booking statistics
   */
  async getBookingStats(): Promise<{
    total: number;
    confirmed: number;
    completed: number;
    cancelled: number;
    byStatus: Record<string, number>;
    bySpace: Record<string, number>;
  }> {
    const allBookings = await this.getAllBookings();
    const statusCount: Record<string, number> = {};
    const spaceCount: Record<string, number> = {};

    allBookings.forEach(booking => {
      statusCount[booking.status] = (statusCount[booking.status] || 0) + 1;
      spaceCount[booking.spaceId] = (spaceCount[booking.spaceId] || 0) + 1;
    });

    return {
      total: allBookings.length,
      confirmed: allBookings.filter(b => b.status === 'Confirmed').length,
      completed: allBookings.filter(b => b.status === 'Completed').length,
      cancelled: allBookings.filter(b => b.status === 'Cancelled').length,
      byStatus: statusCount,
      bySpace: spaceCount,
    };
  }

  // Private helper methods
  private isInDateRange(booking: Booking, startDate?: string, endDate?: string): boolean {
    if (!startDate && !endDate) return true;
    
    const bookingDate = parseISO(booking.date);
    
    if (startDate && isBefore(bookingDate, parseISO(startDate))) return false;
    if (endDate && isAfter(bookingDate, parseISO(endDate))) return false;
    
    return true;
  }

  private hasTimeOverlap(
    booking1: { startTime: string; endTime: string; },
    booking2: { startTime: string; endTime: string; }
  ): boolean {
    const start1 = this.timeToMinutes(booking1.startTime);
    const end1 = this.timeToMinutes(booking1.endTime);
    const start2 = this.timeToMinutes(booking2.startTime);
    const end2 = this.timeToMinutes(booking2.endTime);

    return start1 < end2 && start2 < end1;
  }

  private timeToMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  private initializeSampleData(): void {
    // Only initialize sample data when in sample data mode
    if (isSampleDataMode()) {
      this.demoBookings = [...sampleBookingsData];
    } else {
      this.demoBookings = [];
    }
  }

  private async withDelay<T>(operation: () => Promise<T>): Promise<T> {
    return new Promise(async (resolve, reject) => {
      setTimeout(async () => {
        try {
          const result = await operation();
          resolve(result);
        } catch (error) {
          reject(error);
        }
      }, this.DELAY_MS);
    });
  }
}

// Export singleton instance
export const bookingManager = new BookingManager(); 
